import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../services/auth";
import { stripeApi } from "../services/stripe";
import { AuthContext } from "../context/AuthContext";
import { openStripeUrl, stripeClientFields, onStripeBrowserClosed } from "../lib/stripeFlow";
import FinishSetupScreen from "../components/FinishSetupScreen";
import { T } from "../i18n/T";
import { t } from "../i18n";

/**
 * Destination for talents whose login response carried `needsStripeFinish: true`
 * — i.e. they have a Stripe Connect account but didn't finish onboarding (closed
 * the browser, session timed out, callback URL never fired, etc.). Part of the
 * mandatory-Stripe series; the punch list reskinned it to the ONE shared
 * FinishSetupScreen (no more "Welcome back, name" — same screen as the
 * post-Stripe callback states).
 *
 * What this page does:
 *   1. Clicking "Add bank details" calls POST /stripe/connect (existing endpoint
 *      from the cashout-time prompt). That returns a fresh Stripe Account Link
 *      whose return_url is /profile?stripeReturn=<token> — AuthContext already
 *      handles the token via /auth/exchange-stripe-return on the return trip.
 *   2. The contact-support link is the escape hatch for talents who can't
 *      complete Stripe for any reason (the user is still authenticated, so
 *      they can use any other in-app flow first).
 *   3. Back signs them out and lands on the homepage (punch-list rule: Back and
 *      the header Home link both go to the homepage; signing out first keeps
 *      the landing page out of a half-logged-in state — it doubles as the
 *      wrong-account escape the old /login redirect provided).
 */
const FinishStripePage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const handleContinue = async () => {
    setPending(true);
    setError("");
    try {
      const data = await stripeApi.connect({ ...stripeClientFields() });
      if (data?.url) {
        // Native: Stripe opens in the in-app sheet over this page. If the
        // talent closes it without the return trip firing, re-check their
        // Stripe state — silently move on when onboarding actually finished,
        // otherwise just un-stick the button.
        const stopBrowserWatch = onStripeBrowserClosed(async () => {
          stopBrowserWatch();
          try {
            const fresh = await authApi.user();
            if (fresh?.isStripeOnboarded) {
              navigate("/requests", { replace: true });
              return;
            }
          } catch { /* fall through to un-stick */ }
          setPending(false);
        });
        await openStripeUrl(data.url);
        return;
      }
      setError(t("finishStripePage.couldnTGenerateA"));
      setPending(false);
    } catch (e: any) {
      setError(e?.response?.data?.msg || t("finishStripePage.couldnTReachStripe"));
      setPending(false);
    }
  };

  const handleBack = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <FinishSetupScreen
      ctaTitle={t("finishStripePage.addBankDetails")}
      ctaSub={
        <>
          <T k="finishStripePage.secureFastPayoutsMin" tags={{ 0: (c) => <span className="step5-choice-sub-dot">{c}</span> }} />
        </>
      }
      pendingTitle={t("finishStripePage.redirectingToStripe")}
      pending={pending}
      error={error}
      onCta={handleContinue}
      onBack={handleBack}
    />
  );
};

export default FinishStripePage;
